<section class="page-shell">
  <header class="page-hero dbw-card dbw-card--xl">
    <div>
      <p class="eyebrow">Aramis</p>
      <h3>Intégration des commandes et mouvements APRIL</h3>
      <p class="hero-subtitle">
        Déposez le fichier Excel mensuel du partenaire APRIL, lancez d'abord une simulation pour
        contrôler les rejets, puis intégrez les commandes et mouvements ADI dans Aramis.
        Les rapports des intégrations réalisées et des rejets (un onglet par type) sont
        téléchargeables après chaque exécution.
      </p>
    </div>
  </header>

  @if (!authorized()) {
  <dbw-state-card appearance="error" title="Action non autorisée">
    <p>Vous n'êtes pas autorisé à lancer l'intégration APRIL.</p>
  </dbw-state-card>
  } @else {
  <section class="content-grid">
    <article class="panel dbw-card dbw-card--xl">
      <div class="panel-heading">
        <h4>Fichier d'intégration</h4>
      </div>

      <div class="form-controls">
        <div class="file-uploader-container">
          <oui-file-uploader type="single" accept=".xlsx"
            [value]="file() ? [file()!] : null"
            (valueChange)="onFilesChange($event)" [maxFileSize]="10"
            hint="Fichier attendu : Reporting_De_Facturation (.xlsx) / Taille max : 10 Mo"></oui-file-uploader>

          @if (file(); as selectedFile) {
          <oui-file-item [file]="selectedFile" (delete)="removeFile()"></oui-file-item>
          }
        </div>
      </div>

      <div class="form-actions">
        <oui-button color="secondary" type="outlined" iconSuffix="visibility" (click)="simulate()"
          [disabled]="!canSimulate()">
          @if (simulating()) {
          Simulation en cours…
          } @else {
          Simuler
          }
        </oui-button>
        <oui-button iconSuffix="play_arrow" (click)="requestExecution()" [disabled]="!canExecute()"
          ouiTooltip="L'intégration réelle nécessite une simulation préalable du même fichier">
          @if (executing()) {
          Intégration en cours…
          } @else {
          Intégrer dans Aramis
          }
        </oui-button>
      </div>
    </article>

    @if (awaitingConfirmation()) {
    <dbw-state-card appearance="info" title="Confirmer l'intégration">
      <p>
        Les commandes et mouvements du fichier vont être créés dans Aramis.
        Cette opération n'est pas réversible. Confirmez-vous l'intégration ?
      </p>
      <div class="form-actions">
        <oui-button color="secondary" type="outlined" iconPrefix="close" (click)="cancelExecution()">
          Annuler
        </oui-button>
        <oui-button iconSuffix="play_arrow" (click)="confirmExecution()" [disabled]="executing()">
          Confirmer l'intégration
        </oui-button>
      </div>
    </dbw-state-card>
    }

    @if (error()) {
    <dbw-state-card appearance="error" title="Exécution impossible">
      <p>{{ error() }}</p>
    </dbw-state-card>
    }

    @if (result(); as currentResult) {
    <article class="panel dbw-card dbw-card--xl">
      <div class="panel-heading">
        <h4>
          @if (currentResult.simulation) {
          Résultat de la simulation
          } @else {
          Résultat de l'intégration
          }
        </h4>
        @if (currentResult.rolledBack) {
        <span class="rollback-badge">
          <oui-icon>undo</oui-icon>
          Aucune écriture conservée (rollback)
        </span>
        }
      </div>

      <p>{{ currentResult.message }}</p>

      <dl class="result-metrics">
        <div>
          <dt>Lignes du fichier</dt>
          <dd>{{ currentResult.totalRows }}</dd>
        </div>
        <div>
          <dt>Lignes intégrables</dt>
          <dd>{{ currentResult.acceptedRows }}</dd>
        </div>
        <div>
          <dt>Rejets</dt>
          <dd>{{ currentResult.rejectedRows }}</dd>
        </div>
        <div>
          <dt>Commandes créées</dt>
          <dd>{{ currentResult.createdOrders }}</dd>
        </div>
        <div>
          <dt>Mouvements créés</dt>
          <dd>{{ currentResult.createdMovements }}</dd>
        </div>
      </dl>

      @if (currentResult.rejectionCounts.length > 0) {
      <table class="rejections-table">
        <thead>
          <tr>
            <th>Type de rejet</th>
            <th class="rejections-table__count">Lignes</th>
          </tr>
        </thead>
        <tbody>
          @for (rejection of currentResult.rejectionCounts; track rejection.type) {
          <tr>
            <td>{{ rejection.label }}</td>
            <td class="rejections-table__count">{{ rejection.count }}</td>
          </tr>
          }
        </tbody>
      </table>
      }

      @if (currentResult.hasRejectionReport || currentResult.hasIntegrationReport) {
      <div class="form-actions">
        @if (currentResult.hasIntegrationReport) {
        <oui-button color="secondary" type="outlined" iconSuffix="download"
          (click)="downloadIntegrationReport(currentResult.executionId)"
          [disabled]="downloadingReportId() !== null">
          Télécharger le rapport des intégrations
        </oui-button>
        }
        @if (currentResult.hasRejectionReport) {
        <oui-button color="secondary" type="outlined" iconSuffix="download"
          (click)="downloadRejectionReport(currentResult.executionId)"
          [disabled]="downloadingReportId() !== null">
          Télécharger le rapport de rejets
        </oui-button>
        }
      </div>
      }
    </article>
    }

    <article class="panel dbw-card dbw-card--xl">
      <div class="panel-heading">
        <h4>Historique des exécutions</h4>
        <oui-button color="secondary" type="text" iconSuffix="sync" (click)="loadHistory(historyPageIndex())"
          [disabled]="loadingHistory()">
          Actualiser
        </oui-button>
      </div>

      @if (historyItems().length === 0) {
      <p class="history-empty">Aucune exécution enregistrée.</p>
      } @else {
      <div class="history-table-container">
        <table class="history-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Utilisateur</th>
              <th>Fichier</th>
              <th>Type</th>
              <th class="history-table__count">Lignes</th>
              <th class="history-table__count">Rejets</th>
              <th class="history-table__count">Commandes</th>
              <th class="history-table__count">Mouvements</th>
              <th>Rapports</th>
            </tr>
          </thead>
          <tbody>
            @for (item of historyItems(); track item.id) {
            <tr>
              <td>{{ item.requestedAtUtc | date: 'dd/MM/yyyy HH:mm' }}</td>
              <td>{{ item.requestedBy }}</td>
              <td class="history-table__file">{{ item.sourceFileName }}</td>
              <td>{{ executionStatusLabel(item) }}</td>
              <td class="history-table__count">{{ item.totalRows }}</td>
              <td class="history-table__count">{{ item.rejectedRows }}</td>
              <td class="history-table__count">{{ item.createdOrders }}</td>
              <td class="history-table__count">{{ item.createdMovements }}</td>
              <td>
                @if (item.hasIntegrationReport) {
                <oui-button color="secondary" type="text" iconSuffix="download"
                  (click)="downloadIntegrationReport(item.id)" [disabled]="downloadingReportId() !== null"
                  ouiTooltip="Télécharger le rapport des intégrations">
                  Intégrations
                </oui-button>
                }
                @if (item.hasRejectionReport) {
                <oui-button color="secondary" type="text" iconSuffix="download"
                  (click)="downloadRejectionReport(item.id)" [disabled]="downloadingReportId() !== null"
                  ouiTooltip="Télécharger le rapport de rejets">
                  Rejets
                </oui-button>
                }
                @if (!item.hasIntegrationReport && !item.hasRejectionReport) {
                <span class="history-table__no-report">—</span>
                }
              </td>
            </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="form-actions">
        <oui-button color="secondary" type="outlined" iconPrefix="chevron_left"
          (click)="loadHistory(historyPageIndex() - 1)" [disabled]="!historyHasPrevious() || loadingHistory()">
          Précédent
        </oui-button>
        <oui-button color="secondary" type="outlined" iconSuffix="chevron_right"
          (click)="loadHistory(historyPageIndex() + 1)" [disabled]="!historyHasNext() || loadingHistory()">
          Suivant
        </oui-button>
      </div>
      }
    </article>
  </section>
  }
</section>
